/* eslint-disable react/forbid-prop-types */
import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import BigNumber from 'bignumber.js';

import PropTypes from 'prop-types';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import {
  RATES_QUERY_KEY,
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  USER_PREFERENCES,
} from '../../../../utilities/sharedQueries';
import { formatNumber } from '../../../../utilities/formatHelpers';
import { exchange, currencies } from '../../../../utilities/constants';
import { keysToUpperCase } from '../../../../utilities/helpers';
import { useUserWallets } from '../../hooks';
import { WalletPopupItemPlaceholder } from '../../../../components/Placeholders';
import { walletCurrenciesTypes, WalletScreens } from '../../constants';
import { useLedgerFilteredCurrencies } from '../../../../utilities/sharedHooks/ledgerWallets';
import { useAccountRestriction } from '../../../../utilities/sharedHooks/account';
import AddAccount from '../../partials/AddAccount';
import { useLedgerAccounts } from '../../../../utilities/sharedHooks/ledgerSDKHooks';

const SelectWalletWithdraw = ({
  openScreen,
  openAddAccount,
  openSendCrypto,
  withBack,
  onPrevClick,
}) => {
  const { data: rates, isLoading: isLoadingRates } = useQuery(RATES_QUERY_KEY);
  const { data: userPreferences } = useQuery(USER_PREFERENCES);
  const { restrictions } = useAccountRestriction();
  const [userWallets] = useUserWallets();
  const [ledgerAccounts] = useLedgerAccounts();

  // While restrictions is loading wallets data, it returns default data as an object of three properties
  // To prevent the options to be visible while its loading, this check of Object.keys(restrictions).length <= 3 is needed
  const isLoading = isLoadingRates || Object.keys(restrictions).length <= 3;

  const { t: translate } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const handleSearchInput = (event) => {
    setSearchValue(event.target.value);
  };
  const coins = useMemo(() => {
    const userWalletsCoins = Object.keys(keysToUpperCase(userWallets));
    return userWalletsCoins?.filter((wallet) => !restrictions[wallet?.toLowerCase()]?.sendDisabled);
  }, [restrictions, userWallets]);

  const { data: userPref } = useQuery(USER_PREFERENCES);
  const displayCurrency = userPref?.displayCurrency || 'EUR';
  const _destination = displayCurrency.toUpperCase();

  const getRateForCoin = useCallback(
    (_source) => {
      let _rate = 0;
      if (rates) {
        if (_source === _destination) {
          _rate = 1;
        } else if (rates[`${_source}${_destination}`]) {
          _rate = new BigNumber(rates[`${_source}${_destination}`].ask);
        } else if (rates[`${_destination}${_source}`]) {
          _rate = new BigNumber(1).dividedBy(rates[`${_destination}${_source}`].ask);
        }
      }

      return formatNumber(_rate, currencies[_destination].decimal);
    },
    [_destination, rates]
  );

  const balancesData = useMemo(() => {
    if (userPreferences && userWallets) {
      const result = {};
      const userWalletsUpperCase = keysToUpperCase(userWallets);

      // Override the case of missing rate field.
      Object.keys(userWalletsUpperCase).forEach((coin) => {
        if (!userWalletsUpperCase[coin].rate) {
          userWalletsUpperCase[coin].rate = getRateForCoin(coin.toUpperCase());
        }
      });
      coins.forEach((coin) => {
        const currency = currencies[coin];
        const wallet = userWalletsUpperCase[coin.toUpperCase()];
        const balance = formatNumber(
          wallet?.available_balance * currency.conversion,
          currency.decimal
        );
        result[coin] = formatNumber(wallet?.rate * balance, exchange.FIAT_VALUE);
      });

      return result;
    }

    return null;
  }, [userPreferences, userWallets, coins, getRateForCoin]);

  const sortedCoins = useMemo(
    () =>
      Object.entries(balancesData).sort(
        (a, b) => parseFloat(b[1]) - parseFloat(a[1]) || a[0].localeCompare(b[0])
      ),
    [balancesData]
  );

  const filteredCoins = useMemo(
    () =>
      sortedCoins.filter(
        (coin) =>
          coin[0].search(searchValue.toUpperCase()) >= 0 ||
          currencies[coin]?.fullName?.toUpperCase().search(searchValue.toUpperCase()) >= 0
      ),
    [searchValue, sortedCoins]
  );

  const { data: ledgerFilteredCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const handleItemClick = (coin) => {
    if (
      userWallets[coin?.toLowerCase()]?.wallet_type?.toLowerCase() === walletCurrenciesTypes.FIAT &&
      ledgerFilteredCurrencies &&
      !ledgerFilteredCurrencies[coin?.toLowerCase()]?.fiat_currency_peg
    ) {
      openScreen(WalletScreens.FIAT_WITHDRAWAL, {
        wallet: userWallets[coin.toLowerCase()],
        ledgerAccount: ledgerAccounts[coin.toLowerCase()],
      });
    } else if (ledgerFilteredCurrencies) {
      openSendCrypto(userWallets[coin.toLowerCase()], true);
    }
  };

  const renderHeader = () => (
    <div className="form-group rounded bg-light mb-3 mt-4 p-0">
      <input
        type="text"
        value={searchValue}
        onChange={(e) => handleSearchInput(e)}
        className="form-control-search bg-transparent border-0 ps-5"
        placeholder={translate('select_account_input_placeholder')}
        style={{ height: '2.5rem' }}
      />
      <span className="icon-search icon-search-other text-shadow" />
    </div>
  );

  const renderWalletItem = (coin) => {
    const disabled = restrictions[coin?.toLowerCase()]?.receiveDisabled;
    const availableBalance = balancesData[coin];

    return (
      <div key={coin}>
        <AddAccount
          label={currencies[coin]?.displayName}
          icon={coin}
          subLabel={`${formatNumber(
            availableBalance,
            exchange.FIAT_VALUE
          )} ${userPreferences.displayCurrency.toUpperCase()}`}
          isImg
          imageStyle={{
            height: '1.6rem',
            width: '1.6rem',
            ...(coin?.toUpperCase() === currencies.XRP.icon
              ? {
                  backgroundColor: 'white',
                  padding: '1px',
                  borderRadius: '20px',
                }
              : {}),
          }}
          disabled={disabled}
          noMobile
          className="my-2 bg-light"
          onClick={() => {
            handleItemClick(coin);
          }}
        />
      </div>
    );
  };

  const renderSelectWallet = () =>
    isLoading ? (
      <div className="wallet-list">
        <WalletPopupItemPlaceholder />
        <WalletPopupItemPlaceholder />
        <WalletPopupItemPlaceholder />
        <WalletPopupItemPlaceholder />
      </div>
    ) : (
      <div className="wallet-list">{filteredCoins.map((coin) => renderWalletItem(coin[0]))}</div>
    );

  const renderEmptySelectWallet = () => (
    <div className="text-center h-100 d-flex align-items-center">
      <div className="d-flex-row position-relative" style={{ top: '-2.188rem' }}>
        <div className="mb-3">
          <img src="./assets/images/Bitcoin_Encryption.svg" alt="Bitcoin Encryption" />
        </div>
        <div className="mb-3">
          <h2>{translate('empty_wallet_select')}</h2>
        </div>
        <div className="mb-3 w-100">
          <p className="text-f5 mt-2 text-shadow">{translate('empty_wallet_select_subtext')}</p>
        </div>
        <div className="mt-3">
          <button
            className="btn btn-primary text-f6 rounded-3 py-2"
            onClick={() => {
              if (openAddAccount !== null) {
                openAddAccount();
              }
            }}
            type="button">
            {translate('create_account')}
          </button>
        </div>
      </div>
    </div>
  );

  const renderEmptyHeader = () => (
    <div className="w-100 mt-4">
      <div className="alert alert-info d-flex" role="alert">
        <i className="icon icon-info-icon position-relative start-0" />
        <span className="content">{translate('empty_wallet_select_alert')}</span>
      </div>
    </div>
  );

  const handleRenderHeader = () => {
    if (coins.length === 0) {
      return renderEmptyHeader();
    }
    return renderHeader();
  };

  const handleRenderBody = () => {
    if (filteredCoins.length > 0) return renderSelectWallet();

    if (coins.length === 0) return renderEmptySelectWallet();

    return <></>;
  };
  return (
    <ModalBodyLayout
      withBack={!!withBack}
      onPrevClick={withBack ? onPrevClick : null}
      title={translate('withdraw_select_wallet_label')}
      renderHeader={handleRenderHeader}>
      {handleRenderBody()}
    </ModalBodyLayout>
  );
};

export default SelectWalletWithdraw;

SelectWalletWithdraw.propTypes = {
  openScreen: PropTypes.func.isRequired,
  openAddAccount: PropTypes.func,
  openSendCrypto: PropTypes.func,
  withBack: PropTypes.bool,
  onPrevClick: PropTypes.func,
};
SelectWalletWithdraw.defaultProps = {
  withBack: null,
  openAddAccount: () => {},
  openSendCrypto: () => {},
  onPrevClick: () => {},
};
